import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import lib from '../assets/utils/lib';

/**
 * Custom hook for managing admin notifications
 * Provides functionality for fetching, filtering, and marking notifications as read
 * 
 * @returns {Object} Notification state and actions
 */
export const useNotifications = (options = {}) => {
  const {
    autoRefresh = false,
    refreshInterval = 30000, // 30 seconds default
    pageSize = 50,
    initialFilter = 'all',
  } = options;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState(initialFilter);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize,
    total: 0,
  });
  
  const refreshIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async (page = 1, notificationFilter = filter) => {
    if (!isMountedRef.current) return;

    setLoading(true);
    try {
      const params = {
        page,
        pageSize: pagination.pageSize,
      };

      if (notificationFilter !== 'all') {
        params.type = notificationFilter;
      }

      const response = await lib.getAdminNotifications(params);
      
      if (isMountedRef.current) {
        const body = response?.data || response;
        
        if (parseInt(body?.error) === 0) {
          const notificationsList = Array.isArray(body?.data) ? body.data : [];
          setNotifications(notificationsList);
          setUnreadCount(body?.unread_count || 0);
          setPagination((prev) => ({
            ...prev,
            current: body?.pagination?.page || page,
            total: body?.pagination?.total || notificationsList.length || 0,
          }));
        } else {
          message.error(body?.message || 'Failed to fetch notifications');
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error fetching notifications:', error);
        message.error('Failed to fetch notifications');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [filter, pagination.pageSize]);

  /**
   * Handle filter change
   */
  const handleFilterChange = useCallback((newFilter) => {
    setFilter(newFilter);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, []);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page, pageSize) => {
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
  }, []);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await lib.markNotificationAsRead(notificationId);
      
      if (response?.data?.error === 0) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    const unreadNotifications = notifications.filter((n) => !n.read);
    
    for (const notification of unreadNotifications) {
      await markAsRead(notification.id);
    }
  }, [notifications, markAsRead]);

  /**
   * Refresh notifications
   */
  const refresh = useCallback(() => {
    fetchNotifications(pagination.current, filter);
  }, [fetchNotifications, pagination, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Setup auto-refresh
   */
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(refresh, refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, refresh]);

  /**
   * Fetch notifications on mount and when filter/page changes
   */
  useEffect(() => {
    fetchNotifications(pagination.current, filter);
  }, [pagination.current, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    notifications,
    loading,
    unreadCount,
    filter,
    pagination,
    fetchNotifications,
    handleFilterChange,
    handlePageChange,
    markAsRead,
    markAllAsRead,
    refresh,
  };
};

/**
 * Custom hook for notification preferences
 * 
 * @returns {Object} Preferences state and actions
 */
export const useNotificationPreferences = () => {
  const [preferences, setPreferences] = useState({
    push_enabled: true,
    email_enabled: true,
    sms_enabled: false,
    marketing_enabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMountedRef = useRef(true);

  /**
   * Fetch preferences from API
   */
  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const response = await lib.getNotificationPreferences();
      
      if (isMountedRef.current) {
        const body = response?.data || response;
        
        if (parseInt(body?.error) === 0) {
          setPreferences(body?.data || preferences);
        } else {
          message.error(body?.message || 'Failed to fetch preferences');
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error fetching preferences:', error);
        message.error('Failed to fetch preferences');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Update a single preference
   */
  const updatePreference = useCallback(async (key, value) => {
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);
    
    setSaving(true);
    try {
      const response = await lib.updateNotificationPreferences(newPreferences);
      
      if (isMountedRef.current) {
        if (response?.data?.error === 0) {
          message.success('Preferences updated successfully');
        } else {
          message.error(response?.data?.message || 'Failed to update preferences');
          // Revert on error
          setPreferences(preferences);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error updating preferences:', error);
        message.error('Failed to update preferences');
        // Revert on error
        setPreferences(preferences);
      }
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }, [preferences]);

  /**
   * Update multiple preferences at once
   */
  const updatePreferences = useCallback(async (newPreferences) => {
    setPreferences(newPreferences);
    
    setSaving(true);
    try {
      const response = await lib.updateNotificationPreferences(newPreferences);
      
      if (isMountedRef.current) {
        if (response?.data?.error === 0) {
          message.success('Preferences updated successfully');
        } else {
          message.error(response?.data?.message || 'Failed to update preferences');
          // Revert on error
          setPreferences(preferences);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error updating preferences:', error);
        message.error('Failed to update preferences');
        // Revert on error
        setPreferences(preferences);
      }
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }, [preferences]);

  /**
   * Fetch preferences on mount
   */
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    preferences,
    loading,
    saving,
    updatePreference,
    updatePreferences,
    refresh: fetchPreferences,
  };
};

/**
 * Custom hook for pending claims
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} Pending claims state and actions
 */
export const usePendingClaims = (options = {}) => {
  const { hours = 24, autoRefresh = false, refreshInterval = 3600000 } = options; // 1 hour default

  const [pendingClaims, setPendingClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  
  const refreshIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Fetch pending claims from API
   */
  const fetchPendingClaims = useCallback(async () => {
    if (!isMountedRef.current) return;

    setLoading(true);
    try {
      const response = await lib.getPendingClaims({ hours });
      
      if (isMountedRef.current) {
        const body = response?.data || response;
        
        if (parseInt(body?.error) === 0) {
          const claimsList = Array.isArray(body?.data) ? body.data : [];
          setPendingClaims(claimsList);
          setLastChecked(new Date());
        } else {
          message.error(body?.message || 'Failed to fetch pending claims');
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error fetching pending claims:', error);
        message.error('Failed to fetch pending claims');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [hours]);

  /**
   * Refresh pending claims
   */
  const refresh = useCallback(() => {
    fetchPendingClaims();
  }, [fetchPendingClaims]);

  /**
   * Setup auto-refresh
   */
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(refresh, refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, refresh]);

  /**
   * Fetch on mount
   */
  useEffect(() => {
    fetchPendingClaims();
  }, [fetchPendingClaims]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    pendingClaims,
    loading,
    lastChecked,
    refresh,
    count: pendingClaims.length,
  };
};

/**
 * Custom hook for live statistics
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} Statistics state and actions
 */
export const useLiveStats = (options = {}) => {
  const { autoRefresh = false, refreshInterval = 300000 } = options; // 5 minutes default

  const [stats, setStats] = useState({
    appointments_today: 0,
    appointments_pending: 0,
    pa_requests_today: 0,
    claims_pending: 0,
  });
  const [loading, setLoading] = useState(false);
  
  const refreshIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Fetch stats from API
   */
  const fetchStats = useCallback(async () => {
    if (!isMountedRef.current) return;

    setLoading(true);
    try {
      const response = await lib.getAdminStats();
      
      if (isMountedRef.current) {
        const body = response?.data || response;
        
        if (parseInt(body?.error) === 0) {
          setStats(body?.data || stats);
        } else {
          console.error('Failed to fetch stats:', body?.message);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        console.error('Error fetching stats:', error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Refresh stats
   */
  const refresh = useCallback(() => {
    fetchStats();
  }, [fetchStats]);

  /**
   * Setup auto-refresh
   */
  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(refresh, refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, refresh]);

  /**
   * Fetch on mount
   */
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    stats,
    loading,
    refresh,
    updateStat: useCallback((key, value) => {
      setStats((prev) => ({ ...prev, [key]: value }));
    }, []),
  };
};



